'use server'

import { apiRequest, ApiResponse } from '../request'

const BASE = 'financial/orphan-payments'

/**
 * Pagamentos órfãos: Pix que entrou na conta fora de qualquer cobrança (cobrança
 * `ASAAS_ORPHAN`). Espelha `OrphanPaymentsController` do backend. Valores em centavos.
 */

export type OrphanAppointmentType = 'EXAM' | 'CONSULTATION' | 'PROCEDURE'

export type OrphanMatchKind =
    | 'MANUAL_DUPLICATE'
    | 'EXACT_CHARGE'
    | 'EXACT_REMAINING'
    | 'FITS_FLEX'
    | 'FITS_REMAINING'

export interface OrphanPayment {
    id: number
    companyId: number
    companyName: string | null
    amount: number
    paidAt: string | null
    /** Lido do extrato; `null` enquanto o lançamento do extrato não chegou. */
    payerName: string | null
    description: string | null
}

export interface OrphanSuggestion {
    appointmentType: OrphanAppointmentType
    appointmentId: number
    companyId: number
    clientName: string | null
    title: string | null
    saleDate: string
    sale: number
    received: number
    remaining: number
    flex: boolean
    kind: OrphanMatchKind
    score: number
    reasons: string[]
    possibleDuplicate: { billingId: number; amount: number; paidAt: string | null } | null
    /** Baixa manual ("Outro"/"Pix" à mão) de mesmo valor que o Pix pode substituir. */
    replace: {
        billingId: number
        amount: number
        paidAt: string | null
        paymentMethod: string | null
        /** Paga no mesmo dia do Pix. */
        sameDay: boolean
    } | null
    /** Mesmo valor e mesmo dia de uma baixa manual: o match exato. */
    exactMatch: boolean
}

export interface OrphanPaymentWithSuggestions extends OrphanPayment {
    suggestions: OrphanSuggestion[]
}

export async function getOrphanPaymentsCount(): Promise<ApiResponse<{ count: number }>> {
    return apiRequest<{ count: number }>(`${BASE}/count`, {})
}

export async function getOrphanPayments(): Promise<ApiResponse<OrphanPaymentWithSuggestions[]>> {
    return apiRequest<OrphanPaymentWithSuggestions[]>(BASE, {})
}

export async function getOrphanSuggestions(id: number): Promise<ApiResponse<OrphanSuggestion[]>> {
    return apiRequest<OrphanSuggestion[]>(`${BASE}/${id}/suggestions`, {})
}

/** Pix soltos do mesmo valor nos últimos 60 dias (aviso da baixa manual). */
export async function getOrphanMatches(amount: number): Promise<ApiResponse<OrphanPayment[]>> {
    return apiRequest<OrphanPayment[]>(`${BASE}/matches?amount=${Math.round(amount)}`, {})
}

/** Pix soltos que podem ter pago este atendimento (primeira pergunta do "Gerar cobrança"). */
export async function getOrphansForAppointment(
    appointmentType: OrphanAppointmentType,
    appointmentId: number,
    amount?: number,
): Promise<ApiResponse<Array<OrphanPayment & { exact: boolean }>>> {
    const search = new URLSearchParams({ appointmentType, appointmentId: String(appointmentId) })
    if (amount && amount > 0) search.set('amount', String(Math.round(amount)))
    return apiRequest<Array<OrphanPayment & { exact: boolean }>>(`${BASE}/for-appointment?${search}`, {})
}

export async function linkOrphanPayment(
    id: number,
    appointmentType: OrphanAppointmentType,
    appointmentId: number,
): Promise<ApiResponse<{ mode: string; message: string }>> {
    return apiRequest<{ mode: string; message: string }>(
        `${BASE}/${id}/link`,
        { data: { appointmentType, appointmentId } },
        'post',
    )
}

/**
 * "Substituir": o Pix assume o lugar da baixa manual de mesmo valor do atendimento,
 * herdando vínculo, repasses e complementos; a baixa é excluída sem cancelamento nem
 * estorno, e o caixa não muda. Evita a venda contada duas vezes.
 */
export async function replaceOrphanPayment(
    id: number,
    appointmentType: OrphanAppointmentType,
    appointmentId: number,
    manualBillingId: number,
): Promise<ApiResponse<{ mode: string; message: string; replacedBillingId: number }>> {
    return apiRequest<{ mode: string; message: string; replacedBillingId: number }>(
        `${BASE}/${id}/replace`,
        { data: { appointmentType, appointmentId, manualBillingId } },
        'post',
    )
}

/** "Não é de atendimento": tira o Pix da lista, com o motivo. */
export async function dismissOrphanPayment(
    id: number,
    note: string,
): Promise<ApiResponse<{ billingId: number }>> {
    return apiRequest<{ billingId: number }>(`${BASE}/${id}/dismiss`, { data: { note } }, 'post')
}

/** Um atendimento do vínculo de um Pix com vários atendimentos. */
export interface OrphanLinkManyAppointment {
    appointmentType: OrphanAppointmentType
    appointmentId: number
    /** Consulta zerada por cupom cujo valor um terceiro pagou. */
    thirdPartyCovered?: boolean
}

/** Como cada atendimento entra no vínculo, ou por que não entra. */
export interface OrphanLinkManyRow {
    appointmentType: OrphanAppointmentType
    appointmentId: number
    companyId: number | null
    clientId: number | null
    clientName: string | null
    title: string | null
    /** Venda do atendimento como ele está (com desconto e cupom). */
    sale: number
    couponCode: string | null
    couponDiscount: number
    thirdPartyAllowed: boolean
    thirdPartyCovered: boolean
    /** Parte do Pix deste atendimento. */
    amount: number
    blockedReason: string | null
}

export interface OrphanLinkManyPreview {
    billingId: number
    pixAmount: number
    total: number
    /** Pix menos a soma dos atendimentos. */
    difference: number
    rows: OrphanLinkManyRow[]
    errors: string[]
    canLink: boolean
}

/** Simula o vínculo do Pix com vários atendimentos. Não grava nada. */
export async function previewOrphanLinkMany(
    id: number,
    appointments: OrphanLinkManyAppointment[],
    thirdPartyPayer?: string,
): Promise<ApiResponse<OrphanLinkManyPreview>> {
    return apiRequest<OrphanLinkManyPreview>(
        `${BASE}/${id}/link-many/preview`,
        { data: { appointments, thirdPartyPayer: thirdPartyPayer || undefined } },
        'post',
    )
}

/**
 * Pix único que pagou vários atendimentos (a família, a empresa que sorteou consultas):
 * vincula todos de uma vez. Recusa a operação inteira se algum atendimento tiver
 * cobrança viva ou dinheiro recebido.
 */
export async function linkOrphanMany(
    id: number,
    appointments: OrphanLinkManyAppointment[],
    thirdPartyPayer?: string,
): Promise<ApiResponse<{ billingId: number; count: number; message: string }>> {
    return apiRequest<{ billingId: number; count: number; message: string }>(
        `${BASE}/${id}/link-many`,
        { data: { appointments, thirdPartyPayer: thirdPartyPayer || undefined } },
        'post',
    )
}
